/**
 * Marketing MV2 query API — isolated from MV1 pages/Marketing.jsx.
 * Per-endpoint fetchers (MV1-style resilience).
 */
import api from "@/lib/api";

export const MARKETING_CORE_QUERY_KEY = "marketing-core";
export const MARKETING_ANALYTICS_QUERY_KEY = "marketing-analytics";
export const MARKETING_EXPENSES_QUERY_KEY = "marketing-expenses";
export const MARKETING_SAMPLE_BATCHES_QUERY_KEY = "marketing-sample-batches";
export const MARKETING_TASTING_EVENTS_QUERY_KEY = "marketing-tasting-events";

export const MARKETING_SAMPLE_COST_QUERY_KEY = "marketing-sample-cost";
export const MARKETING_SAMPLES_QUERY_KEY = "marketing-samples";
export const MARKETING_STATS_QUERY_KEY = "marketing-stats";
export const MARKETING_ANALYTICS_DATA_QUERY_KEY = "marketing-analytics-data";
export const MARKETING_GROWTH_QUERY_KEY = "marketing-growth";

export const MARKETING_RECORDS_PER_PAGE = 50;

function isAbortError(error, signal) {
  if (signal?.aborted) return true;
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

async function safeSectionFetch(label, signal, fetcher, fallback) {
  try {
    const data = await fetcher();
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    return { data, error: null };
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    console.error(`Marketing MV2: failed to fetch ${label}:`, error);
    return { data: fallback, error };
  }
}

export async function fetchMarketingSampleCostFromApi(signal) {
  const { data } = await safeSectionFetch(
    "sample-cost",
    signal,
    () => api.get("/marketing/sample-cost", { signal }).then((r) => r.data),
    null
  );
  return data;
}

export async function fetchMarketingSamplesFromApi(currentPage, recordsPerPage, signal) {
  const { data } = await safeSectionFetch(
    "samples",
    signal,
    () =>
      api
        .get(`/marketing/samples?limit=${recordsPerPage}&page=${currentPage}`, { signal })
        .then((r) => ({
          samples: r.data.samples || [],
          totalSamples: r.data.total || 0,
        })),
    { samples: [], totalSamples: 0 }
  );
  return data;
}

export async function fetchMarketingStatsFromApi(signal) {
  const { data } = await safeSectionFetch(
    "stats",
    signal,
    () => api.get("/marketing/stats", { signal }).then((r) => r.data),
    null
  );
  return data;
}

export async function fetchMarketingAnalyticsDataFromApi(analyticsPeriod, signal) {
  const { data } = await safeSectionFetch(
    "analytics",
    signal,
    () =>
      api.get(`/marketing/analytics?period=${analyticsPeriod}`, { signal }).then((r) => r.data),
    null
  );
  return data;
}

export async function fetchMarketingGrowthFromApi(growthDays, signal) {
  const { data } = await safeSectionFetch(
    "growth-comparison",
    signal,
    () =>
      api.get(`/marketing/growth-comparison?days=${growthDays}`, { signal }).then((r) => r.data),
    null
  );
  return data;
}

/** Composed fetch — safe section fetchers; kept for guard wiring. */
export async function fetchMarketingCoreFromApi(currentPage, recordsPerPage, signal) {
  const [costMaster, samplesBundle, stats] = await Promise.all([
    fetchMarketingSampleCostFromApi(signal),
    fetchMarketingSamplesFromApi(currentPage, recordsPerPage, signal),
    fetchMarketingStatsFromApi(signal),
  ]);
  return {
    costMaster,
    samples: samplesBundle?.samples ?? [],
    totalSamples: samplesBundle?.totalSamples ?? 0,
    stats,
  };
}

/** Composed fetch — safe section fetchers; kept for guard wiring. */
export async function fetchMarketingAnalyticsFromApi(analyticsPeriod, growthDays, signal) {
  const [analyticsData, growthData] = await Promise.all([
    fetchMarketingAnalyticsDataFromApi(analyticsPeriod, signal),
    fetchMarketingGrowthFromApi(growthDays, signal),
  ]);
  return { analyticsData, growthData };
}

export async function fetchMarketingExpensesFromApi(signal) {
  const res = await api.get("/marketing/expenses", { signal });
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return {
    expenses: res.data.expenses || [],
  };
}

export async function fetchMarketingSampleBatchesFromApi(signal) {
  const res = await api.get("/marketing/sample-batches", { signal });
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return {
    sampleBatches: res.data.batches || [],
  };
}

export async function fetchMarketingTastingEventsFromApi(signal) {
  const res = await api.get("/marketing/sampling-events", { signal });
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  return {
    tastingEvents: res.data.events || [],
  };
}
